import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { fetchProduct, type ProductDetail } from '../api/catalog';

export interface CartItem {
  id: string;
  name: string;
  coverUrl: string | null;
  platform: string | null;
  region: string;
  priceCents: number;
}

const STORAGE_KEY = 'playstock_cart';
export const PURCHASE_LIMIT = 5;

function loadInitial(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

/** Snapshot the fields the cart cares about from a fresh product payload. */
function toCartItem(p: ProductDetail): CartItem {
  return {
    id: p.id,
    name: p.name,
    coverUrl: p.coverUrl ?? p.imageUrl ?? null,
    platform: p.platform,
    region: p.regions?.[0] || (p.regionalLimitation && p.regionalLimitation !== 'Other' ? p.regionalLimitation : 'GLOBAL'),
    priceCents: p.ourPriceCents,
  };
}

export const useCartStore = defineStore('cart', () => {
  const items = ref<CartItem[]>(loadInitial());
  const validating = ref(false);

  function persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items.value));
    } catch {
      /* ignore quota / unavailable storage */
    }
  }

  const count = computed(() => items.value.length);
  const totalCents = computed(() => items.value.reduce((sum, i) => sum + i.priceCents, 0));
  const isFull = computed(() => items.value.length >= PURCHASE_LIMIT);

  function has(id: string) {
    return items.value.some((i) => i.id === id);
  }

  function add(product: ProductDetail) {
    if (has(product.id) || isFull.value) return;
    items.value.push(toCartItem(product));
    persist();
  }

  /**
   * Re-check every cached item against the backend so the cart never shows stale
   * data: drop products that were removed (404) or are out of stock, and refresh
   * name/price/cover for the rest. Transient errors (network, 429, 5xx) keep the
   * cached item so a single API hiccup never wipes the cart.
   */
  async function validate() {
    if (!items.value.length) return;
    validating.value = true;
    try {
      const snapshot = items.value;
      const results = await Promise.allSettled(snapshot.map((i) => fetchProduct(i.id)));
      const next: CartItem[] = [];
      results.forEach((res, idx) => {
        if (res.status === 'fulfilled') {
          const p = res.value;
          if (p.available === false || p.qty === 0) return; // gone out of stock → drop
          next.push(toCartItem(p));
        } else {
          const status = (res.reason as { response?: { status?: number } })?.response?.status;
          if (status === 404) return; // product no longer exists → drop
          next.push(snapshot[idx]); // transient failure → keep cached snapshot
        }
      });
      items.value = next;
      persist();
    } finally {
      validating.value = false;
    }
  }

  function remove(id: string) {
    items.value = items.value.filter((i) => i.id !== id);
    persist();
  }

  function clear() {
    items.value = [];
    persist();
  }

  return { items, validating, count, totalCents, isFull, has, add, remove, clear, validate };
});
